import type { CSSProperties, ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * The legacy "white rows on grey" list pages (lcert ListCounties / Overview,
 * calibration dates, milestones, capability targets): a #EBEBEB page that
 * scrolls, a centered 1440px column with 2px gaps, an uppercase blue heading
 * row and one white row per item ending in a chevron.
 */

export const LegacyListPage = ({
  children,
  paddingBottom = 56,
  gap = 2,
}: {
  children: ReactNode;
  paddingBottom?: number;
  gap?: number;
}) => (
  <div
    className='rep-list-page rep:relative rep:flex rep:min-h-0 rep:w-full rep:flex-1 rep:flex-col rep:items-center rep:overflow-y-auto'
    style={{ backgroundColor: '#EBEBEB', paddingBottom, boxSizing: 'border-box' }}>
    <div
      className='rep:flex rep:w-full rep:flex-col rep:items-center'
      style={{ maxWidth: 1440, gap, padding: 24, boxSizing: 'border-box' }}>
      {children}
    </div>
  </div>
);

const rowStyle: CSSProperties = {
  display: 'flex',
  width: '100%',
  padding: 16,
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  background: '#FFF',
  boxSizing: 'border-box',
};

export const LegacyListHeading = ({ children }: { children: ReactNode }) => (
  <div style={rowStyle}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 0 0' }}>
      <span style={{ textTransform: 'uppercase', color: '#2961FD', fontWeight: 700, fontSize: 14 }}>
        {children}
      </span>
    </div>
  </div>
);

export const LegacyListRow = ({
  children,
  trailing,
  onClick,
  onChevronClick,
}: {
  /** Row content (left side, flex 1). */
  children: ReactNode;
  /** Tags/chips placed before the chevron. */
  trailing?: ReactNode;
  /** Whole-row click (legacy ListCounties / calibration rows). */
  onClick?: () => void;
  /** Chevron-only click (legacy RowOverviewDetail). */
  onChevronClick?: () => void;
}) => (
  <div
    className='rep-table-row'
    role={onClick ? 'button' : undefined}
    onClick={onClick}
    style={{ ...rowStyle, cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 0 0', minWidth: 0 }}>
      {children}
    </div>
    {trailing}
    <ChevronRight
      role={onChevronClick ? 'button' : undefined}
      aria-label={onChevronClick ? 'Open' : undefined}
      onClick={onChevronClick}
      className='rep:shrink-0 rep:text-[#707070] rep:hover:text-[#2961FD]'
      style={{ cursor: onClick || onChevronClick ? 'pointer' : 'default' }}
    />
  </div>
);

/** The small grey count tag ("3 items"). */
export const LegacyTag = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      display: 'flex',
      alignItems: 'center',
      padding: 4,
      borderRadius: 2,
      background: '#F0F0F0',
      color: '#474747',
      fontSize: 12,
      fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
    {children}
  </span>
);

export const pluralItems = (count: number, noun = 'item') =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;
